import { html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";

import "@phosphor-icons/webcomponents/PhCaretDown";
import "ui-components/form-elements/input/wow-input";
import "ui-components/form-elements/select/wow-select";
import "ui-components/form-elements/switch/switch";
import "ui-components/collapsible-section/collapsible-section";
import "ui-components/button/wow-button";
import "../../co-promotion-toggle/co-promotion-toggle";

import { styles } from "./live-event-optional-toggles.css";
import { SignalElement, inject } from "ui-common/di";
import type {
  ILiveEventFormState,
  ILiveEventFormService,
  UIToggleState,
} from "ui-common/services/live-event-form/types";
import type { IBackofficeFeatureFlagsState } from "../../../services/backoffice-feature-flags/types";
import { handleFormInput } from "ui-common/form/form-utils";
import { t } from "../../../translations/i18n";

@customElement("live-event-optional-toggles")
export class LiveEventOptionalToggles extends SignalElement {
  static override styles = [styles];

  @inject<ILiveEventFormState>("liveEventFormState")
  private state!: ILiveEventFormState;

  @inject<ILiveEventFormService>("liveEventFormService")
  private service!: ILiveEventFormService;

  @inject<IBackofficeFeatureFlagsState>("backofficeFeatureFlagsState")
  private featureFlags!: IBackofficeFeatureFlagsState;

  @state()
  private extraSelectCount = 0;

  @state()
  private communityPickerExpanded = false;

  @state()
  private communitySearch = "";

  private communityPickerInitialized = false;

  private handleToggle(toggle: keyof UIToggleState, event: CustomEvent): void {
    const checked = event.detail.checked;
    this.service.updateUIToggle(toggle, checked);
  }

  private handleLinkSelect(index: number, event: CustomEvent): void {
    const newLinkId = event.detail.value as string;
    if (!newLinkId) return;

    const linkedLinkIds = this.state.linkedLinkIds.get();
    const currentLinkId = linkedLinkIds[index];

    // Remove old selection at this index if exists
    if (currentLinkId) {
      this.service.toggleLinkedLink(currentLinkId, false);
    }

    if (!currentLinkId && this.extraSelectCount > 0) {
      this.extraSelectCount = this.extraSelectCount - 1;
    }

    // Add new selection
    this.service.toggleLinkedLink(newLinkId, true);
  }

  private handleRemoveLink(index: number): void {
    const linkedLinkIds = this.state.linkedLinkIds.get();
    const linkId = linkedLinkIds[index];
    if (linkId) {
      this.service.toggleLinkedLink(linkId, false);
    }
    if (this.extraSelectCount > 0) {
      this.extraSelectCount = this.extraSelectCount - 1;
    }
  }

  private handleAddExtra(): void {
    const availableLinks = this.state.availableLinks.get();
    const linkedLinkIds = this.state.linkedLinkIds.get();
    const selectCount = Math.max(1, linkedLinkIds.length) + this.extraSelectCount;

    if (selectCount >= availableLinks.length) return;

    this.extraSelectCount = this.extraSelectCount + 1;
  }

  private getAvailableOptionsForIndex(index: number) {
    const availableLinks = this.state.availableLinks.get();
    const linkedLinkIds = this.state.linkedLinkIds.get();
    const currentValue = linkedLinkIds[index];

    const linkOptions = availableLinks
      .filter((link) => !linkedLinkIds.includes(link.id) || link.id === currentValue)
      .map((link) => ({
        value: link.id,
        label: link.title,
      }));

    return [{ value: "", label: "" }, ...linkOptions];
  }

  private renderLinkedLinks() {
    const availableLinks = this.state.availableLinks.get();
    const linkedLinkIds = this.state.linkedLinkIds.get();
    const isLoadingLinks = this.state.isLoadingLinks.get();

    if (isLoadingLinks) {
      return html`<div class="loading-links">${t("createLiveEventPage.loadingLinks")}</div>`;
    }

    if (availableLinks.length === 0) {
      return html`<div class="no-links">${t("createLiveEventPage.noLinksAvailable")}</div>`;
    }

    const selectCount = Math.max(1, linkedLinkIds.length) + this.extraSelectCount;
    const hasMoreLinksAvailable = linkedLinkIds.length < availableLinks.length;

    return html`
      <div class="link-selects">
        ${Array.from({ length: selectCount }, (_, i) => {
          const options = this.getAvailableOptionsForIndex(i);
          const currentValue = linkedLinkIds[i] || "";
          const showRemove = i > 0 || linkedLinkIds.length > 1;

          return html`
            <div class="link-select-row">
              <wow-select
                label=${t("createLiveEventPage.selectLink")}
                .options=${options}
                .value=${currentValue}
                @change=${(e: CustomEvent) => this.handleLinkSelect(i, e)}
              ></wow-select>
              ${showRemove
                ? html`
                    <wow-button variant="subtle" size="small" square @click=${() => this.handleRemoveLink(i)}>
                      &times;
                    </wow-button>
                  `
                : nothing}
            </div>
          `;
        })}
        ${hasMoreLinksAvailable
          ? html`
              <button class="add-extra-button" @click=${this.handleAddExtra}>
                ${t("createLiveEventPage.addExtra")}
              </button>
            `
          : nothing}
      </div>
    `;
  }

  private handleCollaboratorsSaved(e: CustomEvent) {
    this.service.setCollaboratorHandles(e.detail.collaborators);
  }

  private renderCoPromotionToggle() {
    if (!this.featureFlags.hasFeature("co-promotions")) return nothing;

    const uiToggles = this.state.uiToggles.get();

    return html`
      <co-promotion-toggle
        ?checked=${uiToggles.showCoPromotion}
        .collaboratorHandles=${this.state.collaboratorHandles.get()}
        .existingCollaborators=${this.state.existingCollaborators.get()}
        @toggle-change=${(e: CustomEvent) => this.handleToggle("showCoPromotion", e)}
        @collaborators-saved=${this.handleCollaboratorsSaved}
      ></co-promotion-toggle>
    `;
  }

  private handleCommunityToggle(event: CustomEvent): void {
    const checked = event.detail.checked;
    this.service.updateUIToggle("showCommunityOnly", checked);
    this.service.setCommunityOnly(checked);
  }

  private renderCommunityPicker() {
    const availableCommunities = this.state.availableCommunities.get();
    const isLoading = this.state.isLoadingCommunities.get();
    const selectedIds = this.state.communityIds.get();
    const search = this.communitySearch.toLowerCase();

    if (!this.communityPickerInitialized && selectedIds.length > 0) {
      this.communityPickerInitialized = true;
      this.communityPickerExpanded = true;
    }

    const filtered = search
      ? availableCommunities.filter((c) => c.label.toLowerCase().includes(search))
      : availableCommunities;

    return html`
      <div class="community-picker">
        <div
          class="community-picker-header"
          @click=${() => (this.communityPickerExpanded = !this.communityPickerExpanded)}
        >
          <div class="community-search-wrapper">
            <input
              class="community-search"
              type="text"
              placeholder=${t("createLiveEventPage.selectCommunities")}
              .value=${this.communitySearch}
              @input=${(e: Event) => {
                this.communitySearch = (e.target as HTMLInputElement).value;
                this.communityPickerExpanded = true;
              }}
              @click=${(e: Event) => e.stopPropagation()}
            />
          </div>
          <span class="community-chevron ${this.communityPickerExpanded ? "expanded" : ""}"
            ><ph-caret-down size="20"></ph-caret-down
          ></span>
        </div>
        ${this.communityPickerExpanded
          ? html`
              <div class="community-picker-list">
                ${isLoading
                  ? html`<div class="community-picker-empty">${t("createLiveEventPage.loadingCommunities")}</div>`
                  : filtered.map(
                      (community) => html`
                        <label class="community-picker-item" @click=${(e: Event) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            .checked=${selectedIds.includes(community.value)}
                            @change=${() =>
                              this.service.toggleCommunity(community.value, !selectedIds.includes(community.value))}
                          />
                          <span class="community-picker-label">${community.label}</span>
                        </label>
                      `,
                    )}
                ${!isLoading && filtered.length === 0
                  ? html`<div class="community-picker-empty">${t("createLiveEventPage.noCommunitiesAvailable")}</div>`
                  : nothing}
              </div>
            `
          : nothing}
      </div>
    `;
  }

  private renderCommunityOnlyToggle() {
    if (!this.featureFlags.hasFeature("communities")) return nothing;

    const uiToggles = this.state.uiToggles.get();
    const validationErrors = this.state.validationErrors.get();

    return html`
      <hr class="section-divider" />
      <div class="toggle-field">
        <wow-switch
          label=${t("createLiveEventPage.communityOnly")}
          ?checked=${uiToggles.showCommunityOnly}
          @change=${this.handleCommunityToggle}
        ></wow-switch>
        ${uiToggles.showCommunityOnly
          ? html`
              ${this.renderCommunityPicker()}
              ${validationErrors.communityIds
                ? html`<div class="validation-error">${validationErrors.communityIds}</div>`
                : nothing}
            `
          : nothing}
      </div>
    `;
  }

  override render() {
    const formData = this.state.formData.get();
    const uiToggles = this.state.uiToggles.get();

    return html`
      <div class="optional-toggles">
        <hr class="section-divider" />
        <wow-collapsible-section
          collapsible
          ?expanded=${uiToggles.showLinkedLinks}
          header=${t("createLiveEventPage.linkDiscounts")}
          subtitle=${t("createLiveEventPage.linkDiscountsSubtitle")}
        >
          ${this.renderLinkedLinks()}
        </wow-collapsible-section>

        <!-- WhatsApp Community Toggle -->
        <hr class="section-divider" />
        <div class="toggle-field">
          <wow-switch
            label=${t("createLiveEventPage.whatsappCommunity")}
            ?checked=${uiToggles.showWhatsApp}
            @change=${(e: CustomEvent) => this.handleToggle("showWhatsApp", e)}
          ></wow-switch>
          ${uiToggles.showWhatsApp
            ? html`
                <wow-input
                  type="url"
                  placeholder=${t("createLiveEventPage.whatsappGroupLink")}
                  .value=${formData.whatsappUrl}
                  @input=${(e: Event) => handleFormInput(this.service, "whatsappUrl", e)}
                ></wow-input>
              `
            : nothing}
        </div>

        <!-- CTA Text Toggle -->
        <hr class="section-divider" />
        <div class="toggle-field">
          <wow-switch
            label=${t("createLiveEventPage.addCtaText")}
            ?checked=${uiToggles.showCtaText}
            @change=${(e: CustomEvent) => this.handleToggle("showCtaText", e)}
          ></wow-switch>
          ${uiToggles.showCtaText
            ? html`
                <wow-input
                  placeholder=${t("createLiveEventPage.ctaPlaceholder")}
                  maxlength="255"
                  .value=${formData.ctaText}
                  @input=${(e: Event) => handleFormInput(this.service, "ctaText", e)}
                ></wow-input>
              `
            : nothing}
        </div>

        ${this.renderCoPromotionToggle()} ${this.renderCommunityOnlyToggle()}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "live-event-optional-toggles": LiveEventOptionalToggles;
  }
}
